import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { CheckCircle, User, Sparkles, Wand2 } from 'lucide-react';

const ExperienceSurveyModal = ({ open, onOpenChange, onComplete, api }) => {
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const options = [
    {
      id: 'never',
      level: 'beginner',
      title: "I never clean data",
      description: "I usually just work with data as it comes",
      icon: User,
      color: 'bg-blue-50 border-blue-200 hover:border-blue-400'
    },
    {
      id: 'sometimes',
      level: 'beginner',
      title: "I do it but I'm not really good at it",
      description: "I spend hours fixing data manually",
      icon: Wand2,
      color: 'bg-purple-50 border-purple-200 hover:border-purple-400'
    },
    {
      id: 'expert',
      level: 'consultant',
      title: "I know how to use formulas and VLOOKUPs",
      description: "I'm comfortable with Excel functions",
      icon: Sparkles,
      color: 'bg-emerald-50 border-emerald-200 hover:border-emerald-400'
    }
  ];

  const handleSubmit = async () => {
    if (!selected) return;
    
    setSaving(true);
    const option = options.find(o => o.id === selected);
    
    try {
      await api.post('/api/auth/update-profile', null, {
        params: {
          experience_level: option.level,
          onboarding_completed: true
        }
      });
      
      onComplete(option.level);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save experience level:', error);
      // Still proceed even if save fails
      onComplete(option?.level || 'beginner');
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" hideClose>
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Welcome to AI Data Cleaner! 👋
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            Help us personalize your experience
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-center text-slate-700 font-medium mb-4">
            How would you describe your experience with data cleaning?
          </p>

          <div className="space-y-3">
            {options.map((option) => {
              const Icon = option.icon;
              const isSelected = selected === option.id;
              
              return (
                <button
                  key={option.id}
                  onClick={() => setSelected(option.id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${option.color} ${
                    isSelected 
                      ? 'ring-2 ring-indigo-500 border-indigo-500' 
                      : ''
                  }`}
                  data-testid={`experience-option-${option.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      isSelected ? 'bg-indigo-100' : 'bg-white/50'
                    }`}>
                      <Icon className={`w-5 h-5 ${
                        isSelected ? 'text-indigo-600' : 'text-slate-500'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-semibold ${
                        isSelected ? 'text-indigo-900' : 'text-slate-800'
                      }`}>
                        {option.title}
                      </p>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {option.description}
                      </p>
                    </div>
                    {isSelected && (
                      <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="pt-2">
          <Button
            onClick={handleSubmit}
            disabled={!selected || saving}
            className="w-full h-12 text-lg bg-indigo-600 hover:bg-indigo-700"
            data-testid="experience-submit-btn"
          >
            {saving ? 'Saving...' : 'Continue'}
          </Button>
          <p className="text-xs text-center text-slate-400 mt-3">
            This helps us calculate how much time you're saving
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExperienceSurveyModal;
